import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, X, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isIOS, isAndroid, isStandalone, canInstall, promptInstall, pushSupported, enablePush } from "@/lib/push";
import { InstallSteps } from "@/components/InstallSteps";

const KEY = "itz_install_dismissed";
const busy = () => document.querySelector('[role="dialog"],[data-testid="lucky-wheel-modal"],[data-testid="level-up-modal"],[data-testid="jackpot-celebration"],[data-testid="redeem-celebration"]');

export function InstallBonus() {
  const { user, refreshUser } = useAuth();
  useEffect(() => {
    if (!user || user.app_installed_at || !isStandalone()) return;
    api.post("/app/installed").then(({ data }) => {
      if (!data.awarded) return;
      setTimeout(refreshUser, 1500);
      setTimeout(() => toast.success(`+${data.awarded} Punkte – Installations-Bonus 🎉`, { description: "Danke, dass du die App installiert hast!" }), 1200);
    }).catch(() => {});
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export function InstallPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [bonus, setBonus] = useState(0);
  const [native, setNative] = useState(canInstall());
  useEffect(() => {
    const on = () => setNative(true);
    window.addEventListener("install-available", on);
    return () => window.removeEventListener("install-available", on);
  }, []);
  useEffect(() => {
    if (user?.app_installed_at || isStandalone() || !(isIOS() || isAndroid())) return;
    if (Date.now() - Number(localStorage.getItem(KEY) || 0) < 7 * 864e5) return;
    let n = 0;
    const iv = setInterval(() => {
      if (++n > 40) return clearInterval(iv);
      if (!busy() && (user || n >= 3)) { clearInterval(iv); setOpen(true); }
    }, 3000);
    api.get("/app/install-bonus").then(({ data }) => setBonus(data.points)).catch(() => {});
    return () => clearInterval(iv);
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const close = () => { localStorage.setItem(KEY, String(Date.now())); setOpen(false); };
  const install = async () => { if (await promptInstall()) setOpen(false); };
  return (
    <div className="fixed inset-0 z-[86] flex items-end sm:items-center justify-center p-4 pb-8 bg-rose-950/50 backdrop-blur-sm wheel-fade" data-testid="install-prompt" style={{ pointerEvents: "auto" }}>
      <div className="redeem-rise relative w-full max-w-sm max-h-[92vh] overflow-y-auto rounded-[2rem] bg-white p-6 pt-12 text-center wheel-frame">
        <button type="button" onClick={close} aria-label="Schließen" data-testid="install-prompt-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        <span className="absolute top-3 left-1/2 -translate-x-1/2 w-[64px] h-[64px] rounded-[1.3rem] shadow-xl shadow-rose-300/60 redeem-gift overflow-hidden">
          <img src="/icon-192.png?v=6" alt="itzPlay" className="w-full h-full" />
        </span>
        <h3 className="mt-14 font-display text-2xl font-extrabold">Hol dir die itzPlay App</h3>
        <p className="mt-1 text-sm text-slate-500">Kostenlos auf deinen Home-Bildschirm – schneller öffnen, Push-Nachrichten und ganz ohne Browserleiste. In 3 Schritten erledigt:</p>
        {bonus > 0 && user && <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-700 text-sm font-extrabold px-3 py-1" data-testid="install-prompt-bonus">🎁 +{bonus} Punkte Bonus</p>}
        {native && !isIOS() ? (
          <button type="button" onClick={install} data-testid="install-prompt-install" className="mt-5 w-full py-3.5 rounded-full pink-gradient text-white text-lg font-extrabold shadow-lg shadow-rose-300 active:scale-95 transition-transform wheel-cta inline-flex items-center justify-center gap-2"><Download className="w-5 h-5" /> Jetzt installieren</button>
        ) : (
          <div className="mt-5" data-testid={isIOS() ? "install-prompt-ios" : "install-prompt-android"}><InstallSteps compact /></div>
        )}
        <Link to="/app" onClick={close} data-testid="install-prompt-more" className="mt-4 block text-sm font-bold text-rose-600">Alle Vorteile der App ansehen →</Link>
        <button type="button" onClick={close} data-testid="install-prompt-later" className="mt-2 text-sm font-semibold text-slate-400">Vielleicht später</button>
      </div>
    </div>
  );
}

const PUSH_KEY = "itz_push_ask_dismissed";

export function PushAfterInstall() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [bonus, setBonus] = useState(0);
  const [busyBtn, setBusyBtn] = useState(false);
  useEffect(() => {
    if (!user || !isStandalone() || !pushSupported() || Notification.permission !== "default") return;
    if (Date.now() - Number(localStorage.getItem(PUSH_KEY) || 0) < 3 * 864e5) return;
    api.get("/app/install-bonus").then(({ data }) => setBonus(user.push_first_at ? 0 : data.push_points)).catch(() => {});
    let n = 0;
    const iv = setInterval(() => {
      if (++n > 40) return clearInterval(iv);
      if (!busy()) { clearInterval(iv); setOpen(true); }
    }, 2500);
    return () => clearInterval(iv);
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const close = () => { localStorage.setItem(PUSH_KEY, String(Date.now())); setOpen(false); };
  const enable = async () => {
    setBusyBtn(true);
    try { await enablePush(); toast.success("Push ist an 🔔"); setOpen(false); setTimeout(refreshUser, 800); }
    catch (e) { toast.error(e.message); close(); }
    finally { setBusyBtn(false); }
  };
  return (
    <div className="fixed inset-0 z-[86] flex items-end sm:items-center justify-center p-4 pb-8 bg-rose-950/50 backdrop-blur-sm wheel-fade" data-testid="push-ask" style={{ pointerEvents: "auto" }}>
      <div className="redeem-rise relative w-full max-w-sm rounded-[2rem] bg-white p-6 pt-12 text-center wheel-frame">
        <button type="button" onClick={close} aria-label="Schließen" data-testid="push-ask-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        <span className="absolute -top-9 left-1/2 -translate-x-1/2 w-[72px] h-[72px] rounded-3xl pink-gradient shadow-xl shadow-rose-300/60 flex items-center justify-center redeem-gift"><BellRing className="w-9 h-9 text-white" /></span>
        <h3 className="font-display text-2xl font-extrabold">Nichts mehr verpassen!</h3>
        <p className="mt-1 text-sm text-slate-500">Erfahre sofort von Spontanaufgaben, Jackpots und Nachrichten deiner Freunde.</p>
        {bonus > 0 && <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-700 text-sm font-extrabold px-3 py-1" data-testid="push-ask-bonus">🎁 +{bonus} Punkte fürs Einschalten</p>}
        <button type="button" onClick={enable} disabled={busyBtn} data-testid="push-ask-enable" className="mt-5 w-full py-3.5 rounded-full pink-gradient text-white text-lg font-extrabold shadow-lg shadow-rose-300 active:scale-95 transition-transform wheel-cta inline-flex items-center justify-center gap-2">
          {busyBtn ? <Loader2 className="w-5 h-5 animate-spin" /> : <BellRing className="w-5 h-5" />} Push einschalten
        </button>
        <button type="button" onClick={close} data-testid="push-ask-later" className="mt-4 text-sm font-semibold text-slate-400">Später</button>
      </div>
    </div>
  );
}
